import type { FormField } from '@/forms/typings';
import type { AppError } from '@/types/errors.interface';
import type { AppDispatch } from '../store';

/** How a remove or clear reports back; each call lands before any refetch replaces the form. */
export type GroupActionOutcome = {
  /** The server took the action. */
  onAccepted: () => void;
  /** The refetch after a lost answer shows the action never took effect. */
  onNotTaken: () => void;
  /** Whether a refetched form shows the action taken; undefined when the refetch failed too. */
  tookEffect: (formFields: FormField[] | undefined) => boolean;
};

/** Runs with a fetched form's fields, or undefined when the fetch failed, before either lands in the store. */
export type OnFormFetched = (formFields: FormField[] | undefined) => void;

/** Calls a fetch's `onFetched`, logging what it throws so the fetch neither fails nor drops its form. */
export const notifyFetched = (
  onFetched: OnFormFetched | undefined,
  formFields: FormField[] | undefined,
) => {
  try {
    onFetched?.(formFields);
  } catch (error) {
    console.error('onFetched callback failed', error);
  }
};

// No answer, or a 5xx, can follow a commit; a 4xx or an answered `false` never does.
const mayHaveTakenEffect = (error: AppError) =>
  error.category === 'network' || error.category === 'server';

/** True when a remove or clear that failed with `error` took effect anyway, as a foreground refetch shows. */
export const tookEffectDespite = async (
  error: AppError,
  dispatch: AppDispatch,
  refetch: (onFetched: OnFormFetched) => (dispatch: AppDispatch) => Promise<void>,
  outcome: GroupActionOutcome | undefined,
): Promise<boolean> => {
  if (!outcome || !mayHaveTakenEffect(error)) return false;

  const settled = { taken: undefined as boolean | undefined };
  const settle: OnFormFetched = formFields => {
    settled.taken = outcome.tookEffect(formFields);
    if (settled.taken) outcome.onAccepted();
    else outcome.onNotTaken();
  };

  try {
    await dispatch(refetch(settle));
  } finally {
    // A refetch that threw never reported, so it settles late, as one that failed.
    if (settled.taken === undefined) settle(undefined);
  }
  return settled.taken === true;
};
